import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Button, Card, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, InputAdornment, MenuItem, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';

import {
    listCpcGovRequests, decideCpcGovRequest, REQUEST_STATUS,
    STATUS_LABEL, STATUS_COLOR, STATUS_FILTERS, ALLOWED_NEXT,
} from '../../services/cpcGovRequests.service';

/**
 * CPC Assessment Requests — the DQ-side queue (API-09).
 *
 * Countries whose Indicators 001-015 are out of date ask DQ to re-run the
 * children's survey. Each request also emails the admin inbox; this page exists
 * so that inbox is not the only record of them.
 *
 * Approve and Decline RECORD A DECISION and nothing else — no workspace, no
 * schedule, no email. DQ still runs the survey by hand. The downstream flow is
 * still being designed, so the decision is stored now and acted on later.
 *
 * Material-UI v4 (this app is not on @mui/material), so styling is `style=`
 * rather than `sx=`.
 */

const fmtDate = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const StatusPill = ({ status }) => {
    const c = STATUS_COLOR[status] || { fg: '#555', bg: '#eee' };
    return (
        <span style={{
            display: 'inline-block', padding: '3px 10px', borderRadius: 12,
            fontSize: 12, fontWeight: 700, color: c.fg, background: c.bg,
            whiteSpace: 'nowrap',
        }}>
            {STATUS_LABEL[status] || status || '—'}
        </span>
    );
};

const CPCGovRequests = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // { request, next } — the row being decided, and the status being set.
    const [pending, setPending] = useState(null);
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    /**
     * Guards against an out-of-order response overwriting a newer one: typing
     * quickly fires several searches, and the slowest can land last.
     */
    const reqId = useRef(0);

    const load = useCallback(async (params) => {
        const mine = ++reqId.current;
        setLoading(true);
        setError('');
        try {
            const res = await listCpcGovRequests(params);
            if (mine !== reqId.current) return;

            if (res && res.data) {
                setRows(res.data.requests || []);
                setTotal(Number(res.data.total) || 0);
            } else {
                setRows([]);
                setTotal(0);
                setError((res && res.message) || 'Could not load requests.');
            }
        } catch (e) {
            if (mine !== reqId.current) return;
            setRows([]);
            setTotal(0);
            setError((e && e.message) || 'Could not load requests.');
        } finally {
            if (mine === reqId.current) setLoading(false);
        }
    }, []);

    /**
     * Debounced: the search runs on the server, so firing on every keystroke
     * would be one query per character.
     */
    useEffect(() => {
        const t = setTimeout(() => {
            load({ search, status, page, rows_per_page: rowsPerPage });
        }, 350);
        return () => clearTimeout(t);
    }, [search, status, page, rowsPerPage, load]);

    const reload = () => load({ search, status, page, rows_per_page: rowsPerPage });

    const onSearch = (e) => {
        setSearch(e.target.value);
        // Page 4 of the previous result set is meaningless against a new search.
        setPage(0);
    };

    const onStatus = (e) => {
        setStatus(e.target.value);
        setPage(0);
    };

    const onRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const openDecision = (request, next) => {
        setPending({ request, next });
        setReason('');
        setSaveError('');
    };

    const confirmDecision = async () => {
        if (!pending) return;
        setSaving(true);
        setSaveError('');
        try {
            const res = await decideCpcGovRequest({
                request_id: pending.request._id,
                status: pending.next,
                decision_note: reason,
            });
            if (res && res.data) {
                setPending(null);
                reload();
            } else {
                setSaveError((res && res.message) || 'Could not save that decision.');
            }
        } catch (e) {
            setSaveError((e && e.message) || 'Could not save that decision.');
        } finally {
            setSaving(false);
        }
    };

    const decliningNow = pending && pending.next === REQUEST_STATUS.DECLINED;
    // The server requires a reason to decline; block the button rather than
    // letting the request go and bounce back as an error.
    const cannotConfirm = saving || (decliningNow && !reason.trim());

    const emptyMessage = search || status
        ? 'No requests match that filter.'
        : 'No country has requested the Children’s Assessment yet.';

    return (
        <Box style={{ padding: 24 }}>
            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 4 }}>
                CPC Assessment Requests
            </Typography>
            <Typography variant="body2" style={{ color: '#666', marginBottom: 20 }}>
                Countries that have asked DQ to run the Children&rsquo;s Assessment
                (Indicators 001&ndash;015). Each request covers the whole assessment for
                that country. Approving or declining records the decision only.
            </Typography>

            <Card style={{ padding: 16 }}>
                <Box style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                    <TextField
                        value={search}
                        onChange={onSearch}
                        placeholder="Search country, name, email, organisation or note"
                        variant="outlined"
                        size="small"
                        style={{ flex: '1 1 320px', maxWidth: 460 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        select
                        value={status}
                        onChange={onStatus}
                        label="Status"
                        variant="outlined"
                        size="small"
                        style={{ width: 180 }}
                    >
                        {STATUS_FILTERS.map((o) => (
                            <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
                        ))}
                    </TextField>
                </Box>

                {error ? (
                    <Typography style={{ color: '#b3261e', fontWeight: 600, padding: '12px 0' }}>
                        {error}
                    </Typography>
                ) : null}

                <TableContainer style={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ fontWeight: 700 }}>Country</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Requested by</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Email</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Organisation</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Note</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Requested on</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" style={{ padding: 40 }}>
                                        <CircularProgress size={26} />
                                    </TableCell>
                                </TableRow>
                            ) : null}

                            {!loading && rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" style={{ padding: 40, color: '#777' }}>
                                        {emptyMessage}
                                    </TableCell>
                                </TableRow>
                            ) : null}

                            {!loading && rows.map((r) => {
                                /*
                                 * `contact` is resolved live from the reviewer's profile;
                                 * `requested_by` is the frozen snapshot from the moment they
                                 * asked. Current details are what an admin needs in order to
                                 * reach someone, so those are shown.
                                 */
                                const by = r.contact || r.requested_by || {};
                                const next = ALLOWED_NEXT[r.status] || [];

                                return (
                                    <TableRow key={r._id} hover>
                                        <TableCell style={{ fontWeight: 700 }}>{r.country_code || '—'}</TableCell>
                                        <TableCell>{by.name || '—'}</TableCell>
                                        <TableCell>{by.email || '—'}</TableCell>
                                        <TableCell>{by.organization || '—'}</TableCell>
                                        <TableCell style={{ maxWidth: 260, whiteSpace: 'pre-wrap' }}>
                                            {r.note || '—'}
                                        </TableCell>
                                        <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.createdAt)}</TableCell>
                                        <TableCell>
                                            <StatusPill status={r.status} />
                                            {/*
                                              * The reason a request was declined belongs next to
                                              * the status — it is the whole content of the
                                              * decision, and hiding it would leave the row saying
                                              * "Declined" with no way to find out why.
                                              */}
                                            {r.status === REQUEST_STATUS.DECLINED && r.decision_note ? (
                                                <div style={{ fontSize: 11, color: '#888', marginTop: 4, maxWidth: 220 }}>
                                                    {r.decision_note}
                                                </div>
                                            ) : null}
                                        </TableCell>
                                        <TableCell style={{ whiteSpace: 'nowrap' }}>
                                            {next.length === 0 ? (
                                                <span style={{ color: '#999', fontSize: 12 }}>
                                                    Decided {fmtDate(r.decided_at)}
                                                </span>
                                            ) : (
                                                <>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        style={{ marginRight: 8, color: '#1b6b3a', borderColor: '#1b6b3a' }}
                                                        onClick={() => openDecision(r, REQUEST_STATUS.APPROVED)}
                                                    >
                                                        Approve
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        style={{ color: '#a33224', borderColor: '#a33224' }}
                                                        onClick={() => openDecision(r, REQUEST_STATUS.DECLINED)}
                                                    >
                                                        Decline
                                                    </Button>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/*
                  * count comes from the SERVER, not rows.length — the table only
                  * ever holds one page, so the pager would otherwise never advance.
                  * onChangePage is the v4 prop name; onPageChange is passed too so
                  * this keeps working if the app moves to v5.
                  */}
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    onChangePage={(e, p) => setPage(p)}
                    onPageChange={(e, p) => setPage(p)}
                    onChangeRowsPerPage={onRowsPerPage}
                    onRowsPerPageChange={onRowsPerPage}
                />
            </Card>

            <Dialog open={!!pending} onClose={() => (saving ? null : setPending(null))} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {decliningNow ? 'Decline this request?' : 'Approve this request?'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" style={{ color: '#555', marginBottom: 12 }}>
                        {pending ? `${pending.request.country_code} — Children’s Assessment (Indicators 001–015).` : ''}
                    </Typography>
                    <Typography variant="body2" style={{ color: '#555', marginBottom: 16 }}>
                        This records the decision only. Nothing is scheduled or sent, and it
                        cannot be changed afterwards.
                    </Typography>

                    <TextField
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        label={decliningNow ? 'Reason (required)' : 'Note (optional)'}
                        placeholder={decliningNow ? 'e.g. the survey ran last month' : ''}
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        minRows={3}
                        required={!!decliningNow}
                    />

                    {saveError ? (
                        <Typography style={{ color: '#b3261e', fontWeight: 600, marginTop: 12 }}>
                            {saveError}
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPending(null)} disabled={saving}>Cancel</Button>
                    <Button
                        onClick={confirmDecision}
                        disabled={cannotConfirm}
                        variant="contained"
                        color="primary"
                    >
                        {saving ? 'Saving…' : (decliningNow ? 'Decline' : 'Approve')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CPCGovRequests;
